// frontend/src/components/Suppliers/SuppliersView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, RefreshCw } from 'lucide-react';
import SupplierStats    from './SupplierStats';
import SupplierFilters  from './SupplierFilters';
import SupplierTable    from './SupplierTable';
import SupplierModal    from '../modal/SupplierModal';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const SuppliersView = () => {
  const [suppliers,   setSuppliers]   = useState([]);
  const [stats,       setStats]       = useState({});
  const [categories,  setCategories]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [modalOpen,   setModalOpen]   = useState(false);
  const [editTarget,  setEditTarget]  = useState(null);

  // Filtres
  const [filters, setFilters] = useState({
    search:      '',
    status:      '',
    category_id: '',
    is_preferred: '',
  });

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search)       params.search      = filters.search;
      if (filters.status)       params.status      = filters.status;
      if (filters.category_id)  params.category_id = filters.category_id;
      if (filters.is_preferred !== '') params.is_preferred = filters.is_preferred;

      const [suppRes, statsRes, catRes] = await Promise.all([
        axios.get(`${API_BASE}/suppliers/`,           { params }),
        axios.get(`${API_BASE}/suppliers/stats`),
        axios.get(`${API_BASE}/suppliers/categories`),
      ]);
      setSuppliers(suppRes.data  || []);
      setStats(statsRes.data     || {});
      setCategories(catRes.data  || []);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleEdit = (supplier) => {
    setEditTarget(supplier);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this supplier? This action cannot be undone.')) return;
    try {
      await axios.delete(`${API_BASE}/suppliers/${id}`);
      fetchAll();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error deleting supplier';
      alert(msg);
    }
  };

  const handleModalClose = (refreshNeeded) => {
    setModalOpen(false);
    setEditTarget(null);
    if (refreshNeeded) fetchAll();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Suppliers</h2>
          <p className="text-gray-400 text-sm mt-0.5">
            Manage your vendors, service providers and recurring charges
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={fetchAll}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700/50 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          <button
            onClick={() => { setEditTarget(null); setModalOpen(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            New Supplier
          </button>
        </div>
      </div>

      {/* Stats */}
      <SupplierStats stats={stats} />

      {/* Filters */}
      <SupplierFilters
        filters={filters}
        setFilters={setFilters}
        categories={categories}
      />

      {/* Table */}
      <SupplierTable
        suppliers={suppliers}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Modal */}
      {modalOpen && (
        <SupplierModal
          supplier={editTarget}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};

export default SuppliersView;